import {
  CUSTOM_FIELD_TYPE,
  FETCH_ITEMS_LIMIT,
  IList as IBEList,
  IListSchema as IBEListSchema,
  LIST_ACCESS_RIGHT_FOR_MOBILE_USER,
} from "fieldpro-tools/dist/src/types";
import _ from "lodash";

import prepareCustomFieldDataForBackend from "containers/workflows/subcategories/activities/utils/prepareCustomFieldDataForBackend";
import TLang from "model/application/Lang";
import {
  ASSIGNATION_TYPE,
  IList,
  IListSchema,
  LIST_ACCESS_RIGHT_FOR_WEB_USER,
  LIST_SCOPE,
} from "model/entities/List";
import { IListItem } from "model/entities/ListItem";
import { clone } from "utils/utils";

import { formatAttributeValue } from "./utils/formatAttributeValue";

export interface IChangeTagParams {
  old_tag: string;
  new_tag: string;
  matrix_tag?: string;
  force?: boolean;
}

export const prepareListForBackend = (l: IList): IBEList => {
  const list: IList = clone(l);

  if (!list.scope) {
    list.scope = LIST_SCOPE.GLOBAL;
  }

  if (!list.mobile_access_right_per_role) {
    list.mobile_access_right_per_role = [];
  }

  if (
    list.mobile_access_right_per_role &&
    list.mobile_access_right_per_role.length != 0
  ) {
    list.mobile_access_right_per_role.forEach((mar: any) => {
      delete mar.index;
    });
  }

  if (!list.web_access_right) {
    list.web_access_right = LIST_ACCESS_RIGHT_FOR_WEB_USER.FULL_ACCESS;
  }

  if (!list.mobile_access_right) {
    list.mobile_access_right = LIST_ACCESS_RIGHT_FOR_MOBILE_USER.FULL_ACCESS;
  }

  if (
    !list.hasOwnProperty("can_update_items_ownership") ||
    list.can_update_items_ownership == null
  ) {
    list.can_update_items_ownership = false;
  }

  // replace "label" by "value" in options
  list.schema = prepareListSchemaForBackend(list.schema) as any;

  // remove all 'key' from the schema
  // remove all empty properties from the schema
  list.schema.forEach((attr) => {
    // @ts-expect-error TS7053
    delete attr["key"];
  });

  list.schema = _.map(list.schema, (att) => {
    for (const prop in att) {
      if (
        // @ts-expect-error TS7053
        att[prop] == null ||
        // @ts-expect-error TS7053
        att[prop] === "" ||
        // @ts-expect-error TS7053
        (Array.isArray(att[prop]) && !att[prop].length)
      ) {
        // @ts-expect-error TS7053
        delete att[prop];
      }

      // NOTE: there is a bug, conditions are mutated somewhere before being sent to the BE
      // getExpressions returns a value without "nextOperator".
      if (att.conditions) {
        att.conditions = removeNextOperator(att.conditions);
      }
    }
    return att;
  });

  delete (list as any).linked_workflows;
  delete (list as any).query;
  delete (list as any).deprecated_attributes;
  delete (list as any).options;
  return list as any as IBEList;
};

export const removeNextOperator = <T extends {}>(expressions: T[]) => {
  return _.map(expressions, (expression) => _.omit(expression, "nextOperator"));
};

export const prepareListSchemaForFrontend = (
  schema: IBEListSchema[],
): IListSchema[] => {
  return schema.map((att: any) => {
    if (att.type === CUSTOM_FIELD_TYPE.MATRIX_ON_LIST) {
      att.matrix.typed_questions = prepareListSchemaForFrontend(
        att.matrix.typed_questions,
      );
    }
    if (att.options) {
      att.options = att.options.map((opt: any, index: number) => {
        opt.label = opt.value;
        delete opt.value;
        opt.index = index;

        return opt;
      });
    }
    if (att.deprecatedOptions) {
      att.deprecatedOptions = att.deprecatedOptions.map(
        (opt: any, index: number) => {
          opt.label = opt.value;
          delete opt.value;
          opt.index = index;
          return opt;
        },
      );
    }
    return att;
  });
};

export const prepareOptionsForDeprecatedAttributes = (
  schema: IBEListSchema[],
) => {
  return schema.map((att: any) => {
    if (att.options) {
      att.deprecatedOptions = att.options.filter((opt: any) => opt.deprecated);
      att.options = att.options.filter((opt: any) => !opt.deprecated);
    }
    return att;
  });
};

export const prepareListSchemaForBackend = (
  schema: IListSchema[],
): IBEListSchema[] => {
  return schema.map((att: any) => {
    if (att.type === CUSTOM_FIELD_TYPE.MATRIX_ON_LIST) {
      att.matrix.typed_questions = prepareListSchemaForBackend(
        att.matrix.typed_questions,
      );
    }
    if (att.options) {
      att.options = att.options.map((opt: any) => {
        opt.value = opt.label;
        delete opt.index;
        delete opt.label;
        return opt;
      });
    }
    if (att.deprecatedOptions) delete att.deprecatedOptions;
    return att;
  });
};

export const prepareListsForFrontend = (lists: IBEList[]): IList[] => {
  let result: IBEList[] = clone(lists);
  result = result.map((l) => {
    // add active:true if no "active" attribute
    if (!l.hasOwnProperty("active")) {
      l.active = true;
    }
    // @ts-expect-error TS7053
    if (l["items"]) {
      // @ts-expect-error TS7053
      l["items"] = l["items"].filter((item: any) => item._active === true);
    }
    // @ts-expect-error TS7053
    l["big_list"] = l["item_count"] >= FETCH_ITEMS_LIMIT;
    l["mobile_access_right_per_role"] = !(l as any).mobile_access_right_per_role
      ? []
      : (l as any).mobile_access_right_per_role.map(
          (mar: any, index: number) => ({
            ...mar,
            index: index,
          }),
        );

    // temp fix to support old lists with EDIT_ONLY, EDIT_ONLY will be migrated in the next release.
    if (l.mobile_access_right === LIST_ACCESS_RIGHT_FOR_MOBILE_USER.EDIT_ONLY) {
      l.mobile_access_right = LIST_ACCESS_RIGHT_FOR_MOBILE_USER.READ_AND_EDIT;
    }
    return l;
  });
  // if there is no index, add an index to the attributes
  const compareFct = (a: any, b: any) => {
    if (!a) return -1;
    if (!b) return 1;
    if (a < b) return -1;
    return 1;
  };
  result = result.map((l) => {
    let newSchema = l.schema.filter((att) => att.deprecated);
    const indexes = l.schema.map((attr) => attr.index);
    newSchema = newSchema.concat(
      l.schema
        .filter((att) => !att.deprecated)
        .sort(compareFct)
        .map((att) => {
          if (att.options) {
            (att as any).deprecatedOptions = att.options.filter(
              (opt) => opt.deprecated,
            );
            att.options = att.options.filter((opt) => !opt.deprecated);
          }
          if (att.index !== undefined) {
            return att;
          } else {
            let i = 0;
            while (indexes.includes(i)) {
              i++;
            }
            indexes.push(i);
            return { ...att, index: i };
          }
        })
        .sort((a, b) => (a.index || 0) - (b.index || 0)),
    );
    l.schema = newSchema;
    return l;
  });
  // reformat options: change "value" to "label"
  result = result.map((l: any) => {
    l.schema = prepareListSchemaForFrontend(l.schema) as any;
    l.deprecated_attributes = prepareOptionsForDeprecatedAttributes(
      l.schema.filter((l: any) => l.deprecated),
    );
    return l;
  });
  return result as any[] as IList[];
};

// TODO: extract map
// TODO2: extract formatCustomFieldForBackend
// TODO3: check it works for MC, SC (and add SCOL, MCOL too ?)

export const prepareItemsForBackend = (
  items: IListItem[],
  listSchema: IListSchema[],
) => {
  if (!listSchema) {
    throw new Error("Missing listSchema in prepareItemsForBackend");
  }
  return _.map(items, (item) => {
    const formattedItem: any = {};
    if (item._id) formattedItem._id = item._id;
    if (item._owners) formattedItem._owners = item._owners;

    listSchema.forEach((attSchema) => {
      const attrKey = attSchema.column_tag;
      if (item.hasOwnProperty(attrKey)) {
        formattedItem[attrKey] = prepareCustomFieldDataForBackend({
          question: attSchema,
          answer: item[attrKey],
          listMultipleChoice: true,
        });
      }
    });

    return formattedItem;
  });
};

export const prepareItemsForFrontend = (
  items: IListItem[],
  listSchema: IListSchema[],
  lang: TLang,
  allLists?: IList[],
): IListItem[] => {
  return items.map((item) => {
    item["_active"] = item["_active"] ?? true;

    for (const att of listSchema) {
      if (!att.deprecated) {
        const formattedValue = formatAttributeValue({
          item,
          schema: listSchema,
          attribute: att,
          allLists: allLists ?? [],
          lang,
        });
        item[att.column_tag] = formattedValue;
      }
    }

    return item;
  });
};

export const updateListSchemaByTag = (
  list: IList,
  newTag: string,
  oldTag: string,
  parent?: string,
): IList => {
  const listCopy = { ...list };

  const updatedTypedAttributes = (schema?: IListSchema[]): IListSchema[] => {
    return _.map(schema ?? [], (sch) => ({
      ...sch,
      column_tag: sch.column_tag === oldTag ? newTag : sch.column_tag,
    }));
  };

  if (!parent) {
    return {
      ...list,
      schema: updatedTypedAttributes(listCopy.schema),
    };
  }

  const parentSchema = _.find(listCopy.schema, { column_tag: parent }) as
    | IListSchema
    | undefined;
  if (!parentSchema) return listCopy;
  const typed_questions = updatedTypedAttributes([
    ...(parentSchema.matrix?.typed_questions as IListSchema[]),
  ]);
  const newParentSchema = {
    ...parentSchema,
    matrix: {
      ...parentSchema.matrix,
      typed_questions,
    },
  };

  return {
    ...list,
    schema: _.map(listCopy.schema, (sch) => ({
      ...(sch.column_tag === parent ? newParentSchema : sch),
    })),
  } as IList<IListItem>;
};

export const swapItemOwners = (
  originOwnerIds: string[],
  destinationOwnerIds: string[],
  items: IListItem[] = [],
): IListItem[] => {
  return items.map((item) => {
    if (!item._owners) return item;
    let newOwners = [...item._owners];

    const hasOriginId = _.some(originOwnerIds, (id) =>
      _.includes(item._owners, id),
    );
    const hasDestinationId = _.some(destinationOwnerIds, (id) =>
      _.includes(item._owners, id),
    );

    if (hasOriginId && hasDestinationId) {
      newOwners.push(...originOwnerIds, ...destinationOwnerIds);
    } else if (hasOriginId) {
      newOwners = newOwners.filter((id) => !originOwnerIds.includes(id));
      newOwners.push(...destinationOwnerIds);
    } else if (hasDestinationId) {
      newOwners = newOwners.filter((id) => !destinationOwnerIds.includes(id));
      newOwners.push(...originOwnerIds);
    }

    return {
      ...item,
      _owners: _.uniq([...newOwners]),
    };
  });
};

export const unassignItemOwners = (
  items: IListItem[],
  ownerIds: string[],
  itemIds?: string[],
): IListItem[] => {
  if (_.isEmpty(ownerIds)) {
    return items;
  }

  return _.map(items, (item) => {
    const currentOwners = item._owners ?? [];

    if (!_.isEmpty(itemIds)) {
      if (_.includes(itemIds, item._id)) {
        const updatedOwners = _.filter(
          currentOwners,
          (owner) => !_.includes(ownerIds, owner),
        );
        return { ...item, _owners: updatedOwners };
      }
      return item;
    }

    const updatedOwners = _.filter(
      currentOwners,
      (owner) => !_.includes(ownerIds, owner),
    );
    return { ...item, _owners: updatedOwners };
  });
};

export const assignItemsOwners = (
  destinationOwnerIds: string[],
  type: ASSIGNATION_TYPE,
  items: IListItem[] = [],
  originOwnerIds?: string[],
  itemIds?: string[],
): IListItem[] => {
  return items.map((item) => {
    if (shouldModifyItem(item, originOwnerIds, itemIds)) {
      const updatedOwners = getUpdatedOwners(
        item,
        destinationOwnerIds,
        type,
        originOwnerIds,
        itemIds,
      );
      return { ...item, _owners: updatedOwners };
    }
    return item;
  });
};

const shouldModifyItem = (
  item: IListItem,
  originOwnerIds?: string[],
  itemIds?: string[],
): boolean => {
  if (originOwnerIds) {
    return _.some(originOwnerIds, (id) => _.includes(item._owners, id));
  }
  if (itemIds) {
    return _.includes(itemIds, item._id);
  }
  return false;
};

const getUpdatedOwners = (
  item: IListItem,
  destinationOwnerIds: string[],
  type: ASSIGNATION_TYPE,
  originOwnerIds?: string[],
  itemIds?: string[],
): string[] => {
  let updatedOwners = _.clone(item._owners) || [];

  if (type === ASSIGNATION_TYPE.ASSIGN_COPY) {
    const newOwnerIds = destinationOwnerIds.filter(
      (destinationId) => !_.includes(updatedOwners, destinationId),
    );

    updatedOwners.push(...newOwnerIds);
  } else if (type === ASSIGNATION_TYPE.ASSIGN_TRANSFER) {
    if (_.includes(itemIds, item._id)) {
      updatedOwners = [];
    } else {
      updatedOwners = _.filter(
        updatedOwners,
        (owner) => !_.includes(originOwnerIds, owner),
      );
    }

    destinationOwnerIds.forEach((destinationOwnerId) => {
      if (!_.includes(updatedOwners, destinationOwnerId)) {
        updatedOwners.push(destinationOwnerId);
      }
    });
  }

  return updatedOwners;
};

export const formatCreateEditItemsError = (
  error: { message: string },
  lang: TLang,
): string => {
  const uniqueAttributeRegex = /key value violates unique constraint "([^"]+)"/;
  const uniqueField = uniqueAttributeRegex.exec(error.message);

  if (uniqueField) {
    return lang.containers.lists.subCategories.items.createEditModal
      .uniqueFieldError.title;
  }

  return error.message;
};
